import React, { useCallback, useEffect, useMemo, useRef, useState, } from 'react'
import { getCoreRowModel, useReactTable } from '@tanstack/react-table';
import '../RightSide/configurationModal/tableConfig.css';
import './Table.less';
import { useStore } from '../../store/store';
import TableHead from './TableHead';
import TableBody from './TableBody';
import NavbarTable from './NavbarTable';
import { useTranslation } from 'react-i18next';
import TableFooter from './TableFooter';
import { defaultTableConfiguration, viewerTypes, tableTitleWithOptions } from '../../constants/constants';
import ButtonRefresh from '../common/ButtonRefresh';
import {
  fetchTableData,
  rectToKeys,
  rowKeysToRect,
  columnKeysToRect,
  isMultiSelectClick,
  computeLabelRowMerges,
  computeRowStyles,
  getLabelRowMergeState,
  withStructureRowKinds,
  toggleLabelRowOverride,
  canMergeSelection,
  canUnmergeSelection,
  customRowStyles,
  mergeTableCells,
  unmergeTableCells,
  selectionHasOnlyManualRows,
  canGroupColumns,
  canUngroupColumns,
  groupColumns,
  ungroupColumns,
  rowGroupKeys,
} from '../../utils/table';
import { isAVariableBox } from '../../utils/boxes';
import { leftShownStatus, useOverlay } from '../../contexts/OverlayContext';
import { gridUnit, pageHeight } from '../../constants/gridConfig';
import { findTableStyle } from '../../utils/styles';
import { usePreviewContext } from '../../contexts/PreviewContext';
import Loader from '../../Loader';
import InlineSwitch from '../common/InlineSwitch';
import BoxActionsPanel from '../common/BoxActionsPanel';
import BoxFormControlsPortal from '../RightSide/BoxFormControlsPortal';
import { TableInsertionPanel } from './CellInsertionPanel';
import { clearInsertionTarget } from './insertionTarget';
import { getTableRowStructure } from '../../shared/tables/tableDescriptors';
import { zoneSplitTableName } from '../../shared/tables/zoneSplit';

export const MIN_COLUMN_WIDTH = 20;
export const DEFAULT_COLUMN_WIDTH = 100;
const selector = (state) => ({
  landscape: state.landscape,
  configuration: state.configuration,
  moveBoxInfinite: state.moveBoxInfinite,
});
const Table = ({ box, drawMode, verticalAlign }) => {
  const [selectedItem, setSelectedItem] = useState(box.selectedItem || { rowKey: null, columnKey: null, type: null });
  const { startFrom, duppedBreakIdx, fromViewer } = usePreviewContext();
  const { landscape, configuration, moveBoxInfinite } = useStore(selector);
  const updateBox = useStore(({ updateBox }) => updateBox);
  const fetchTable = useStore(({ fetchTable }) => fetchTable)
  const cacheTableRaw = useStore(({ cacheTableRaw }) => cacheTableRaw)
  const setTableColumnWidthsFromCanvas = useStore(({ setTableColumnWidthsFromCanvas }) => setTableColumnWidthsFromCanvas)
  const setBoxMovable = useStore(({ setBoxMovable }) => setBoxMovable)
  const handleSelectBox = useStore(({ handleSelectBox }) => handleSelectBox)
  const { updateLeftShownStatus, overlayProps } = useOverlay();
  const shownStatus = overlayProps?.left?.shownStatus
  const currentDocument = useStore(({ currentDocument }) => currentDocument)
  const defaultTableStyleId = useStore((state) => state.configuration.tableStyles[0].id)
  const isSelected = useStore(({ selectedBoxId }) => selectedBoxId === box.id)
  // Drawer de configuration ouvert sur ce tableau : il cache le tableau, le
  // panel de droite règle alors son aperçu (cf. TableConfigDrawer).
  const isConfiguring = useStore(({ tableConfigBoxId }) => tableConfigBoxId === box.id)
  const isAdmin = useStore(({ user }) => user?.isAdmin)
  const configTableStyle = useStore((state) => (findTableStyle(state.configuration, state?.tmp, box.tableStyleId)))
  const tableStyle = useMemo(() => ({
    ...defaultTableConfiguration, ...configTableStyle
  }), [configTableStyle])

  const storeColumns = useStore((state) => state.columns);
  const splittableTables = useStore((state) => state.splittableTables);
  const zoneSplitTables = useStore((state) => state.zoneSplitTables);

  const { t } = useTranslation();

  const tableRef = useRef()
  const [theadHeight, setTheadHeight] = useState(0)
  const [controlRowsClicked, setControlRowsClicked] = useState([]);

  // Fusion manuelle de cellules (tables custom uniquement)
  const isCustomTable = !box.tableName;
  // Sélection par INDICES (immunité aux clés de lignes dupliquées).
  const [cellSelection, setCellSelection] = useState(null); // { r0, r1, c0, c1 } | null
  // Mode plage active (déclenché par shift+clic) : commande le highlight bleu.
  // Un clic simple pose une sélection 1-case sans highlight (juste pour révéler
  // « Défusionner »).
  const [cellRangeMode, setCellRangeMode] = useState(false);
  const cellAnchorRef = useRef(null); // { rowIndex, colIndex }
  // La sélection de cases courante découle-t-elle des poignées (numéros de
  // ligne, bandeaux d'en-tête de colonne) ?
  const handleSelectionRef = useRef(false);
  const mergedCells = box.content.mergedCells;
  const selectionRect = cellRangeMode ? cellSelection : null;

  const columns = useMemo(() => {
    if (!(box.content?.columns?.length > 0)) return [];

    return box.content.columns.map((col) => {
      return {
        accessorKey: col.key,
        size: col.width,
      };
    });
  }, [box.content.columns]);

  const initializeColumnSizeVars = useCallback(() => {
    const colSizes = {};
    columns.forEach(({ size, accessorKey }) => {
      colSizes[`--${accessorKey}-size`] = size
    })
    return colSizes
  }, [columns])

  const initColumnSizeVars = useMemo(() => {
    return initializeColumnSizeVars()
  }, [initializeColumnSizeVars])

  const [columnSizeVars, setColumnSizeVars] = useState(initColumnSizeVars)

  const data = useMemo(() => {
    const { rowsSelectionStart: rowStart, rowsSelectionEnd: rowEnd } = box.content;
    const boxData = box.content?.data.map((val, idx) => ({ ...val, realRowIdx: idx }));
    let start;

    if (rowStart !== undefined) {
      start = startFrom !== null ? (startFrom + 1) + rowStart : rowStart;
    } else {
      start = startFrom !== null ? (startFrom + 1) : undefined;
    }

    let end;
    if (rowEnd !== undefined) {
      end = duppedBreakIdx !== null ? duppedBreakIdx + rowEnd : rowEnd;
    } else {
      end = duppedBreakIdx !== null ? duppedBreakIdx : undefined;
    }

    if (end !== undefined) {
      end = duppedBreakIdx !== null && startFrom !== null ? startFrom + end : end;
    }

    const finalData = boxData.slice(start - 1 || 0, end || boxData.length) || [];

    if (fromViewer === viewerTypes.PREVIEW && finalData?.[0]?.hiddenColumnsKeys?.length > 0) {
      finalData[0].hiddenColumnsKeys = []
    }
    return finalData;
  }, [box.content, startFrom, duppedBreakIdx, fromViewer]);

  // Tableaux variables à structure déclarée (titres, totaux… cf.
  // getTableRowStructure) : fusionne automatiquement les lignes-titre et les
  // lignes vides, selon les réglages `tableConfig.merge` (exceptions choisies
  // dans le Drawer ou ici). Combiné aux fusions existantes ; les tables custom
  // gardent leurs fusions manuelles telles quelles. Les autres tableaux
  // variables n'ont que des lignes de données : un planning, dont l'image n'est
  // pas dans la case, y passerait pour une ligne-titre et la perdrait.
  // Les lignes ajoutées à la main (`manual`) ne sont jamais fusionnées.
  const mergeOptions = box.tableConfig?.merge;
  const rowStructure = useMemo(
    // Découpée par zone, la box lit la version « par zones » de son tableau,
    // dont chaque zone ouvre sur sa ligne de titre.
    () => (isCustomTable ? null : getTableRowStructure(
      box.content?.shouldSplit ? zoneSplitTableName(box.tableName) : box.tableName,
      box.tableConfig,
    )),
    [isCustomTable, box.tableName, box.tableConfig, box.content?.shouldSplit]
  );
  // Lignes avec leur type déduit (titre, TVA), que visent fusion et style.
  const structuredData = useMemo(
    () => (rowStructure ? withStructureRowKinds(data, box.content.columns, rowStructure) : null),
    [rowStructure, data, box.content.columns]
  );
  const effectiveMergedCells = useMemo(() => {
    if (!structuredData) return mergedCells;
    const autoMerges = computeLabelRowMerges(structuredData, box.content.columns, mergeOptions);
    if (autoMerges.length === 0) return mergedCells;
    return [...(mergedCells ?? []), ...autoMerges];
  }, [structuredData, mergedCells, box.content.columns, mergeOptions]);

  // Style des lignes réglé dans la configuration (`tableConfig.rowStyles`), par
  // clé de ligne : recalculé au rendu, il suit les lignes après rafraîchissement.
  // Un tableau custom range le sien sur chaque ligne.
  const rowStyles = box.tableConfig?.rowStyles;
  const rowStylesByKey = useMemo(() => {
    if (isCustomTable) return customRowStyles(data);
    return structuredData ? computeRowStyles(structuredData, box.content.columns, rowStyles, rowStructure) : null;
  }, [isCustomTable, data, structuredData, box.content.columns, rowStyles, rowStructure]);

  // Ligne-titre de la cellule sélectionnée : le panneau propose de la fusionner
  // ou non, pour toutes les lignes de même libellé.
  const selectedLabelRow = useMemo(() => {
    if (!rowStructure || selectedItem.type !== 'data' || !selectedItem.rowKey) return null;
    const row = box.content.data.find((r) => r.key === selectedItem.rowKey);
    const [typed] = withStructureRowKinds(row ? [row] : [], box.content.columns, rowStructure);
    return getLabelRowMergeState(typed, box.content.columns, mergeOptions);
  }, [rowStructure, selectedItem, box.content.data, box.content.columns, mergeOptions]);

  // Écrit l'exception directement sur la box, sans passer par applyTableConfig :
  // ce dernier rematérialise les lignes depuis l'API, ce qui effacerait les
  // lignes ajoutées à la main et leurs fusions.
  const toggleSelectedLabelRowMerge = useCallback(() => {
    if (!selectedLabelRow) return;
    updateBox(box.id, (b) => {
      const merge = b.tableConfig?.merge ?? {};
      b.tableConfig = {
        ...(b.tableConfig ?? {}),
        merge: { ...merge, overrides: toggleLabelRowOverride(merge.overrides, selectedLabelRow) },
      };
    });
  }, [box.id, selectedLabelRow, updateBox]);

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    enableColumnResizing: false,
    getRowId: (row) => row.key
  })

  // Conversion indices -> clés, seulement pour les actions fusion/défusion.
  const selectionKeys = useMemo(
    () => rectToKeys(data, box.content.columns, cellSelection),
    [data, box.content.columns, cellSelection]
  );

  // Fusion manuelle : toute case d'une table custom ; dans un tableau variable,
  // seulement les lignes ajoutées à la main (`manual`), les autres étant
  // remplacées à chaque rafraîchissement.
  const canSelectCellAt = useCallback(
    (rowIndex) => isCustomTable || !!data[rowIndex]?.manual,
    [isCustomTable, data]
  );
  const selectionMergeable = isCustomTable || selectionHasOnlyManualRows(data, selectionKeys);

  const handleControlRowClick = useCallback(
    ({ parentRowId, parentRowType, shiftKey, type, widthPercent }) => {
      if (type === 'setWidthPercent' && parentRowType === 'header') {
        updateBox(box.id, (b) => {
          const cols = b.content?.columns
          if (!cols || !Array.isArray(cols) || cols.length < 2) return
          const targetIndex = cols.findIndex((c) => c.key === parentRowId)
          if (targetIndex === -1) return

          // Voisine impactée : colonne de droite par défaut, sinon celle de gauche pour la dernière colonne
          const neighborIndex =
            targetIndex === cols.length - 1 ? targetIndex - 1 : targetIndex + 1
          if (neighborIndex < 0 || neighborIndex >= cols.length) return

          const totalWidth = cols.reduce((sum, c) => sum + (c.width ?? 0), 0)
          if (totalWidth <= 0) return

          const targetCurrent = cols[targetIndex].width ?? 0
          const neighborCurrent = cols[neighborIndex].width ?? 0

          const othersTotal = totalWidth - targetCurrent - neighborCurrent

          // Largeur souhaitée pour la colonne cible (en absolu)
          let targetNew = (widthPercent / 100) * totalWidth

          // Contraintes de min width pour cible et voisine
          const minTarget = MIN_COLUMN_WIDTH
          const minNeighbor = MIN_COLUMN_WIDTH

          const maxTarget = totalWidth - othersTotal - minNeighbor
          const minAllowedTarget = minTarget

          if (maxTarget <= minAllowedTarget) return

          if (targetNew < minAllowedTarget) targetNew = minAllowedTarget
          if (targetNew > maxTarget) targetNew = maxTarget

          const neighborNew = totalWidth - othersTotal - targetNew
          if (neighborNew < minNeighbor) return

          cols[targetIndex].width = targetNew
          cols[neighborIndex].width = neighborNew
        })
        // Tableau variable : sync la largeur (px -> %) dans tableConfig
        // (même valeur que le Drawer). Lit les px fraîchement écrits ci-dessus.
        // No-op pour une table custom.
        setTableColumnWidthsFromCanvas(box.id)
        return
      }

      // Lignes fusionnées jusqu'à la dernière colonne : une seule poignée, qui
      // agit sur tout le groupe (sélection, puis suppression ou déplacement).
      const groupOf = (rowId) =>
        parentRowType === 'cell'
          ? rowGroupKeys(data, box.content.columns, effectiveMergedCells, rowId)
          : [rowId]
      const expandGroups = (selection) => {
        const seen = new Set()
        return selection.flatMap((row) =>
          groupOf(row.parentRowId)
            .filter((id) => !seen.has(id) && seen.add(id))
            .map((id) => ({ ...row, parentRowId: id }))
        )
      }

      setControlRowsClicked((prev) => {
        const sameType = prev.filter((row) => row.parentRowType === parentRowType)
        const hasOtherType = prev.some((row) => row.parentRowType !== parentRowType)
        const group = groupOf(parentRowId)

        // Si on change de type (ligne <-> colonne), on repart d'une sélection propre
        const baseSelection = hasOtherType ? [] : sameType

        // Sélection continue avec Shift
        if (shiftKey) {
          // Déterminer l'ancre (dernière sélection du même type ou élément courant)
          const anchorId = baseSelection.length > 0 ? baseSelection[baseSelection.length - 1].parentRowId : parentRowId

          if (parentRowType === 'header') {
            const orderedIds = box.content.columns.map((c) => c.key)
            const startIndex = orderedIds.indexOf(anchorId)
            const endIndex = orderedIds.indexOf(parentRowId)
            if (startIndex === -1 || endIndex === -1) return prev
            const [from, to] = startIndex <= endIndex ? [startIndex, endIndex] : [endIndex, startIndex]
            const rangeIds = orderedIds.slice(from, to + 1)
            return rangeIds.map((id) => ({ parentRowId: id, parentRowType }))
          }

          if (parentRowType === 'cell') {
            const orderedIds = data.map((row) => row.key)
            const startIndex = orderedIds.indexOf(anchorId)
            const endIndex = orderedIds.indexOf(parentRowId)
            if (startIndex === -1 || endIndex === -1) return prev
            const [from, to] = startIndex <= endIndex ? [startIndex, endIndex] : [endIndex, startIndex]
            const rangeIds = orderedIds.slice(from, to + 1)
            return expandGroups(rangeIds.map((id) => ({ parentRowId: id, parentRowType })))
          }

          return prev
        }

        // Sélection simple / toggle (du groupe entier pour des lignes fusionnées)
        const exists = baseSelection.some(
          (row) => row.parentRowId === parentRowId && row.parentRowType === parentRowType
        )

        if (exists) {
          return prev.filter(
            (row) => !(group.includes(row.parentRowId) && row.parentRowType === parentRowType)
          )
        }

        return [...baseSelection, ...group.map((id) => ({ parentRowId: id, parentRowType }))]
      })
    },
    [box.id, box.content.columns, data, effectiveMergedCells, setControlRowsClicked, updateBox, setTableColumnWidthsFromCanvas]
  )

  // Retourne true si le clic a été consommé comme sélection multi-cellules
  // (fusion) -> la cellule n'entre alors PAS en édition.
  const handleClickInTable = useCallback(
    ({ rowKey, columnKey, type, rowIndex, colIndex }) => (e) => {
      // Quand on clique dans une cellule / un header, on annule la sélection de lignes/colonnes
      if (controlRowsClicked.length > 0) {
        setControlRowsClicked([]);
        document.querySelectorAll('[data-drag-row-cell].table-border-hovered, [data-col-cell].table-border-hovered')
          .forEach(el => el.classList.remove('table-border-hovered'))
        document.querySelectorAll('.control-row-wrapper.cr-hovered')
          .forEach(el => el.classList.remove('cr-hovered'))
      }

      // Un clic sur une case reprend la main sur la sélection issue des
      // poignées (cf. l'effet plus bas) au lieu de la laisser l'effacer.
      if (type === 'data' && rowIndex != null && colIndex != null) {
        handleSelectionRef.current = false;
      }

      // Shift+clic sur une cellule fusionnable (cf. canSelectCellAt) : mode
      // plage. Sélectionne la box si besoin, surligne dès la 1ère case, étend
      // ensuite le rectangle depuis l'ancre. Travail par INDICES (clés
      // potentiellement dupliquées). Jamais d'entrée en édition.
      if (type === 'data' && rowIndex != null && colIndex != null && isMultiSelectClick(e) && canSelectCellAt(rowIndex)) {
        if (!isSelected) {
          handleSelectBox({ box, fromViewer });
        }
        const anchor = cellAnchorRef.current;
        if (anchor) {
          setCellSelection({
            r0: anchor.rowIndex,
            r1: rowIndex,
            c0: anchor.colIndex,
            c1: colIndex,
          });
        } else {
          // 1ère case de la plage : devient l'ancre, surlignée seule.
          cellAnchorRef.current = { rowIndex, colIndex };
          setCellSelection({ r0: rowIndex, r1: rowIndex, c0: colIndex, c1: colIndex });
        }
        setCellRangeMode(true);
        e.preventDefault();
        setBoxMovable(false);
        return true;
      }

      // Clic simple sur une cellule fusionnable : (ré)ancre, sélection 1-case
      // sans highlight (permet « Défusionner » si elle appartient à une fusion).
      // Ailleurs (ligne d'API d'un tableau variable), on lâche la sélection
      // précédente pour ne pas laisser « Défusionner » sur l'ancienne case.
      if (type === 'data' && rowIndex != null && colIndex != null) {
        if (canSelectCellAt(rowIndex)) {
          cellAnchorRef.current = { rowIndex, colIndex };
          setCellSelection({ r0: rowIndex, r1: rowIndex, c0: colIndex, c1: colIndex });
        } else {
          cellAnchorRef.current = null;
          setCellSelection(null);
        }
        setCellRangeMode(false);
      }

      if (!isSelected) {
        updateBox(box.id, (selectedBox) => {
          selectedBox.selectedItem = { rowKey, columnKey, type }
        });
      }
      setSelectedItem({ rowKey, columnKey, type })
      e.preventDefault();
      setBoxMovable(false);
      return false;
    },
    [box, canSelectCellAt, isSelected, handleSelectBox, fromViewer, setBoxMovable, updateBox, controlRowsClicked.length, setControlRowsClicked, setSelectedItem]
  );

  // Table désélectionnée -> on purge la sélection de cellules et l'ancre.
  useEffect(() => {
    if (!isSelected) {
      clearInsertionTarget(box.id);
      setCellSelection(null);
      setCellRangeMode(false);
      cellAnchorRef.current = null;
    }
  }, [isSelected, box.id]);

  // Changement structurel (ajout/suppression de lignes ou colonnes) -> les
  // indices de sélection deviennent périmés : on purge.
  const rowCount = data.length;
  const colCount = box.content.columns.length;
  useEffect(() => {
    setCellSelection(null);
    setCellRangeMode(false);
    cellAnchorRef.current = null;
  }, [rowCount, colCount]);

  // Sélection de lignes par leur numéro (poignées de gauche) ou de colonnes par
  // leur bandeau d'en-tête = shift+clic sur toutes leurs cases : on en tire la
  // même sélection de cases, donc la ligne ou la colonne entière est
  // fusionnable. Poignées non contiguës (sélection par bascule) ou lignes non
  // sélectionnables : rien à fusionner. Déclaré après la purge structurelle
  // pour que, à l'ajout d'une ligne ou colonne, elle se reconstruise.
  const dataRef = useRef(data);
  dataRef.current = data;
  const columnsRef = useRef(box.content.columns);
  columnsRef.current = box.content.columns;
  useEffect(() => {
    const handleKeys = (type) => controlRowsClicked
      .filter(({ parentRowType }) => parentRowType === type)
      .map(({ parentRowId }) => parentRowId);
    const rowKeys = handleKeys('cell');
    const columnKeys = handleKeys('header');
    if (rowKeys.length === 0 && columnKeys.length === 0) {
      // Poignées relâchées (re-clic, glisser-déposer, suppression) : la
      // sélection de cases qui en découlait part avec.
      if (handleSelectionRef.current) {
        handleSelectionRef.current = false;
        setCellSelection(null);
        setCellRangeMode(false);
        cellAnchorRef.current = null;
      }
      return;
    }
    handleSelectionRef.current = true;
    const rect = rowKeys.length > 0
      ? rowKeysToRect(dataRef.current, rowKeys, colCount)
      : columnKeysToRect(columnsRef.current, columnKeys, rowCount);
    const selectable = rect && Array.from(
      { length: rect.r1 - rect.r0 + 1 },
      (_, i) => rect.r0 + i
    ).every((i) => isCustomTable || !!dataRef.current[i]?.manual);
    if (!selectable) {
      setCellSelection(null);
      setCellRangeMode(false);
      cellAnchorRef.current = null;
      return;
    }
    cellAnchorRef.current = { rowIndex: rect.r0, colIndex: rect.c0 };
    setCellSelection(rect);
    setCellRangeMode(true);
  }, [controlRowsClicked, rowCount, colCount, isCustomTable]);

  const mergeCells = useCallback(() => {
    if (!selectionKeys) return;
    updateBox(box.id, (b) => mergeTableCells(b.content, selectionKeys));
    setCellSelection(null);
    setCellRangeMode(false);
    cellAnchorRef.current = null;
    setControlRowsClicked([]);
  }, [box.id, selectionKeys, updateBox]);

  const unmergeCells = useCallback(() => {
    if (!selectionKeys) return;
    updateBox(box.id, (b) => unmergeTableCells(b.content, selectionKeys));
    setCellSelection(null);
    setCellRangeMode(false);
    cellAnchorRef.current = null;
    setControlRowsClicked([]);
  }, [box.id, selectionKeys, updateBox]);

  // Colonnes sélectionnées via les poignées d'en-tête (ControlRow), seule
  // entrée possible du groupement : la sélection de cellules ne remonte pas
  // jusqu'aux en-têtes.
  const selectedHeaderKeys = useMemo(
    () =>
      (controlRowsClicked ?? [])
        .filter((row) => row.parentRowType === 'header')
        .map((row) => row.parentRowId),
    [controlRowsClicked]
  );

  const groupHeaderColumns = useCallback(() => {
    updateBox(box.id, (b) => {
      b.content.columnsDisposition = groupColumns(
        b.content.columnsDisposition,
        b.content.columns,
        selectedHeaderKeys,
        t('table-group-default-title')
      );
    });
    setControlRowsClicked([]);
  }, [box.id, selectedHeaderKeys, updateBox, t]);

  const ungroupHeaderColumns = useCallback(() => {
    updateBox(box.id, (b) => {
      b.content.columnsDisposition = ungroupColumns(
        b.content.columnsDisposition,
        selectedHeaderKeys
      );
    });
    setControlRowsClicked([]);
  }, [box.id, selectedHeaderKeys, updateBox]);

  const moveRow = useCallback((draggedRowKey, overRowKey) => {
    if (draggedRowKey === overRowKey) return;
    // Lignes fusionnées jusqu'à la dernière colonne : leur poignée déplace tout
    // le groupe, d'un bloc, et la cible est le début de son propre groupe.
    const movedKeys = rowGroupKeys(data, box.content.columns, effectiveMergedCells, draggedRowKey);
    const targetKey = rowGroupKeys(data, box.content.columns, effectiveMergedCells, overRowKey)[0];
    if (movedKeys.includes(targetKey)) return;
    updateBox(box.id, (b) => {
      const data = b.content?.data;
      if (!data || !Array.isArray(data)) return;
      const dragIdx = data.findIndex((r) => r.key === movedKeys[0]);
      const overIdx = data.findIndex((r) => r.key === targetKey);
      if (dragIdx === -1 || overIdx === -1) return;
      const rows = data.splice(dragIdx, movedKeys.length);
      // En déplaçant vers le bas (dragIdx < overIdx), le splice ci‑dessus décale les indices :
      // la ligne cible est maintenant à overIdx - movedKeys.length. On insère donc là pour
      // « au-dessus de la ligne cible ».
      const insertIdx = dragIdx < overIdx ? overIdx - movedKeys.length : overIdx;
      data.splice(insertIdx, 0, ...rows);

      // Réordonner une ligne appartenant à une fusion casse la contiguïté du
      // rectangle : on retire ces fusions, sauf celles déplacées en entier. Le
      // contenu vit déjà dans l'ancre (les couvertes ont été vidées à la
      // fusion) -> rien à restaurer.
      if (Array.isArray(b.content.mergedCells) && b.content.mergedCells.length > 0) {
        b.content.mergedCells = b.content.mergedCells.filter((m) => {
          const rowKeys = m.rowKeys ?? [];
          if (!rowKeys.some((k) => movedKeys.includes(k))) return true;
          return rowKeys.every((k) => movedKeys.includes(k));
        });
      }
    });
  }, [box.id, box.content.columns, data, effectiveMergedCells, updateBox]);

  const updateTheadHeight = useCallback(() => {
    if (tableRef.current && !!tableRef.current.querySelector("thead"))
      setTheadHeight(tableRef.current.querySelector("thead").getBoundingClientRect().height)
  }, [])

  useEffect(() => {
    updateTheadHeight()
  }, [updateTheadHeight])

  useEffect(() => {
    box.content.columns.forEach((col, index) => {
      if (!col.width) {
        const colWidth = box.width - box.content.columns.reduce((acc, col) => {
          const widthToAdd = col.width ?? 0;
          return acc + widthToAdd;
        }, 0);
        if (colWidth) {
          updateBox(box.id, (selectedBox) => {
            selectedBox.content.columns[index].width = colWidth;
          });
        }
      }
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (typeof box.tableStyleId === 'undefined') {
      updateBox(box.id, (selectedBox) => {
        selectedBox.tableStyleId = defaultTableStyleId;
      });
    }
  }, [box.id, box.tableStyleId, updateBox, defaultTableStyleId]);

  const convertAlign = useCallback((colAlign) => {
    if (colAlign === 'center') return 'center'
    if (colAlign === "end") return 'right'
    if (colAlign === 'justify') return 'justify'
    return 'left'
  }, [])

  const convertVerticalAlign = useCallback((colVerticalAlign) => {
    if (colVerticalAlign === 'center') return 'center'
    if (colVerticalAlign === 'bottom') return 'flex-end'
    return 'flex-start'
  }, [])

  const isAdminTable = isAdmin && currentDocument.type === 'template' && box.tableName
  // Titre du tableau enrichi des options DONNÉE non-défaut (ex. « BPU — Par
  // postes · Prix de vente horaire ») : le nom évolue avec la config du Drawer.
  const adminTableTitle = useMemo(
    () => tableTitleWithOptions(box.tableName, box.tableConfig, t),
    [t, box.tableName, box.tableConfig]
  );
  const isInDrawModeColumn = !!storeColumns?.find((c) => c.id === box?.columnId)?.drawMode;
  const isSplittableTable =
    isAdminTable &&
    Array.isArray(splittableTables) &&
    splittableTables.some((t) => {
      if (typeof t === 'string') return t === box?.tableName;
      return t?.tableName === box?.tableName || t?.table_name === box?.tableName;
    });
  // Un tableau annoncé découpable par zone règle son découpage dans le Drawer
  // « Configurer le tableau » : pas de second interrupteur.
  const shouldShowSplitSwitch = !!(
    isSplittableTable &&
    !isInDrawModeColumn &&
    !zoneSplitTables?.includes(box.tableName)
  );
  const marginLeft = configuration.margins.left * Math.round(gridUnit)
  const marginRight = configuration.margins.right * Math.round(gridUnit)
  const tableWidth = (landscape ? (pageHeight - (marginLeft + marginRight)) : box.content?.columns?.length > 0
    ? box.content.columns.reduce((acc, col) => acc + col.width, 0)
    : box.width);

  const effectiveTableWidth = useMemo(() => {
    if (!drawMode) return tableWidth;
    const total = Object.values(columnSizeVars).reduce((acc, v) => acc + v, 0);
    return total > 0 ? total : tableWidth;
  }, [drawMode, columnSizeVars, tableWidth]);

  // Refs to access latest box values without stale closures
  const prevEffectiveWidthRef = useRef(null);
  const boxLeftRef = useRef(box.left);
  const boxTopRef = useRef(box.top);
  const boxIdRef = useRef(box.id);
  const boxRotationRef = useRef(box.rotation);
  useEffect(() => {
    boxLeftRef.current = box.left;
    boxTopRef.current = box.top;
    boxIdRef.current = box.id;
    boxRotationRef.current = box.rotation;
  }, [box.left, box.top, box.id, box.rotation]);

  // When the table is rotated and its width changes (last column resize), compensate box.left/box.top
  // so the top-left corner of the table stays visually fixed (same as Resizer does for text boxes).
  // Formula: Δleft = -dw/2*(1-cos(R)), Δtop = -dw/2*sin(R)
  useEffect(() => {
    if (!drawMode) return;
    const prevWidth = prevEffectiveWidthRef.current;
    prevEffectiveWidthRef.current = effectiveTableWidth;
    if (prevWidth === null || prevWidth === effectiveTableWidth) return;
    const rotation = boxRotationRef.current;
    if (!rotation) return;
    const dw = effectiveTableWidth - prevWidth;
    const R = rotation * Math.PI / 180;
    const newLeft = Math.round(boxLeftRef.current - dw / 2 * (1 - Math.cos(R)));
    const newTop = Math.round(boxTopRef.current + dw / 2 * Math.sin(R));
    moveBoxInfinite(newLeft, newTop, boxIdRef.current);
  }, [effectiveTableWidth, drawMode, moveBoxInfinite]);

  useEffect(() => {
    setColumnSizeVars(initializeColumnSizeVars())
  }, [initializeColumnSizeVars]);

  const computeMaxColumnWidth = useCallback((colKey) => {
    const columnsSizeArray = Object.entries(columnSizeVars)
    return columnsSizeArray.reduce((acc, [key, value], idx) => {
      if (key === `--${colKey}-size`) {
        if (idx !== columnsSizeArray.length - 1 && columnsSizeArray[idx + 1][1] > MIN_COLUMN_WIDTH)
          return acc + value + columnsSizeArray[idx + 1][1]
        return acc + value
      }
      return acc
    }, 0)
  }, [columnSizeVars])

  const handleOnClickRefresh = () => {
    fetchTableData({
      fetchStoreFunc: fetchTable,
      updateBoxFunc: updateBox,
      box: {
        ...box,
        tableName: box.tableName,
      },
      split: box.content?.shouldSplit,
      recursiveRowId: box.content?.recursiveRowId,
      refresh: true,
      // Rafraîchit aussi le cache brut -> les éditions de présentation locales
      // qui suivent repartent des données à jour, pas d'une version périmée.
      onRawTable: (table) => cacheTableRaw(box.id, table),
      // force=true : déclenche la régénération des screenshots de planning côté
      // back (ignoré par les autres tableaux). La nouvelle image arrive via le
      // broadcast WS planning_screenshots_ready -> re-fetch auto.
      force: true,
    });
  }

  return (
    <>
      <div ref={tableRef}
        onDoubleClick={(event) => {
          if (isAVariableBox(box?.type)) {
            updateLeftShownStatus(leftShownStatus["VARIABLES"], box.id)
          }
          event.stopPropagation()
        }}
        style={{
          position: 'relative',
          width: effectiveTableWidth,
          ...columnSizeVars
        }}>
        {isAdminTable && (
          <table
            style={{
              width: "100%"
            }}
          >
            <thead style={{ backgroundColor: "#c8c8dd", height: 40 }}>
              <tr>
                <th style={{ borderBottom: "1px solid #c0c0d5" }}>
                  {adminTableTitle}
                </th>
              </tr>
            </thead>
            <tbody style={{ backgroundColor: "#cccce1", height: 30 }}>
              <tr>
                <td>
                  {shouldShowSplitSwitch && (
                    <InlineSwitch
                      stopPropagation
                      style={{ justifyContent: 'center', gap: 8 }}
                      start={
                        <span style={{ whiteSpace: "nowrap", fontSize: 12 }}>
                          {t('table-split-modal-non-split')}
                        </span>
                      }
                      end={
                        <span style={{ whiteSpace: "nowrap", fontSize: 12 }}>
                          {t('table-split-modal-split')}
                        </span>
                      }
                      size="small"
                      checked={!!box?.content?.shouldSplit}
                      onChange={(checked) => {
                        updateBox(box.id, (b) => {
                          if (!b.content) b.content = {};
                          b.content.shouldSplit = checked;
                          // Un identifiant de zone n'a de sens que sur une box découpée.
                          if (!checked) delete b.content.identifiers;
                        });
                      }}
                    />
                  )}
                </td>
              </tr>
            </tbody>
          </table>
        )}
        {box.tableName && currentDocument.type !== 'template' && isSelected &&
          <div style={{
            position: 'absolute',
            zIndex: 999,
            top: -12,
            // Au-delà du bouton de suppression de ligne (DeleteButton, 15px hors
            // du bord droit) pour ne pas le chevaucher.
            right: -40,
            pointerEvents: box.isFetching ? 'none' : 'auto',
            opacity: box.isFetching ? 0.3 : 1,
          }}>
            <ButtonRefresh handleOnClickRefresh={handleOnClickRefresh} />
          </div>
        }
        {!isAdminTable &&
          <table style={{
            width: "100%", tableLayout: 'fixed', opacity: box.isFetching ? 0.3 : 1,
            pointerEvents: box.isFetching ? 'none' : 'auto',
          }}>
            <TableHead
              drawMode={drawMode}
              table={table}
              box={box}
              columns={box.content.columns}
              isSelected={isSelected}
              boxId={box.id}
              handleClickInTable={handleClickInTable}
              updateTheadHeight={updateTheadHeight}
              tableStyle={tableStyle}
              convertAlign={convertAlign}
              convertVerticalAlign={convertVerticalAlign}
              rotation={box.rotation}
              setColumnSizeVars={setColumnSizeVars}
              columnSizeVars={columnSizeVars}
              computeMaxColumnWidth={computeMaxColumnWidth}
              controlRowsClicked={controlRowsClicked}
              setControlRowsClicked={setControlRowsClicked}
              onControlRowClick={handleControlRowClick}
              verticalAlign={verticalAlign}
              firstRowStyle={rowStylesByKey?.get(data[0]?.key)}
            />
            <TableBody
              drawMode={drawMode}
              table={table}
              columns={box.content.columns}
              isSelected={isSelected}
              boxId={box.id}
              box={box}
              handleClickInTable={handleClickInTable}
              tableStyle={tableStyle}
              theadHeight={theadHeight}
              data={data}
              convertAlign={convertAlign}
              convertVerticalAlign={convertVerticalAlign}
              computeMaxColumnWidth={computeMaxColumnWidth}
              setColumnSizeVars={setColumnSizeVars}
              rotation={box.rotation}
              controlRowsClicked={controlRowsClicked}
              setControlRowsClicked={setControlRowsClicked}
              verticalAlign={verticalAlign}
              onControlRowClick={handleControlRowClick}
              moveRow={moveRow}
              mergedCells={effectiveMergedCells}
              rowStylesByKey={rowStylesByKey}
              selectionRect={selectionRect}
            />
            <TableFooter
              columnsLength={box.content.columns.length}
              tableStyle={tableStyle}
            />
          </table>
        }
        {box.isFetching && <Loader style={{
          top: 0,
          width: '100%',
          height: '100%',
          position: 'absolute',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1,
          pointerEvents: 'none',
        }} imageStyle={{ width: 80, pointerEvents: 'none' }} />}
      </div>
      {isSelected && (shownStatus !== "variables" || box.fromViewer === viewerTypes.VARIABLES) && (
        <>
          {/* Section « Insertion » unique, branchée sur la cellule ou l'en-tête
              en cours d'édition (cf. insertionTarget). order={0} / order={1} :
              elle passe avant les réglages du tableau, sans dépendre de l'ordre
              de montage DOM. */}
          {!isConfiguring && <TableInsertionPanel boxId={box.id} order={0} />}
          {!isConfiguring && <BoxFormControlsPortal layout="row" order={1}>
            <NavbarTable
              selectedBox={box}
              controlRowsClicked={controlRowsClicked}
              setControlRowsClicked={setControlRowsClicked}
              selectedItem={selectedItem}
              canMergeCells={selectionMergeable && canMergeSelection(selectionKeys, mergedCells)}
              canUnmergeCells={selectionMergeable && canUnmergeSelection(selectionKeys, mergedCells)}
              onMergeCells={mergeCells}
              onUnmergeCells={unmergeCells}
              selectedLabelRow={selectedLabelRow}
              onToggleLabelRowMerge={toggleSelectedLabelRowMerge}
              canGroupColumns={
                isCustomTable &&
                canGroupColumns(selectedHeaderKeys, box.content.columns, box.content.columnsDisposition)
              }
              canUngroupColumns={
                isCustomTable && canUngroupColumns(selectedHeaderKeys, box.content.columnsDisposition)
              }
              onGroupColumns={groupHeaderColumns}
              onUngroupColumns={ungroupHeaderColumns}
            />
          </BoxFormControlsPortal>}
          <BoxActionsPanel />
        </>
      )}
    </>
  )
}

export default Table
